import { Stack } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import BasePopover from '@/components/ui/BasePopover';
import Chip from '@/components/ui/Chip';
import TableColumnClickableText from '@/components/ui/table/TableColumnClickableText';

import type { LocationInstalledMachines } from '@/types';

// Keeps the cell's row height compact and predictable regardless of how many ATMs a location
// (e.g. a warehouse) holds -- anything past this renders behind a "+N more" popover instead of
// stacking one chip per machine.
const VISIBLE_ATM_LIMIT = 2;

interface LocationInstalledAtmsProps {
  installedAtms: LocationInstalledMachines[] | undefined | null;
}

/**
 * "ATM(s) Installed" table cell. Shared by both installed_atms column definitions in
 * constants/app/locations.tsx so the warehouse case (many installed ATMs) degrades the same way
 * everywhere it can appear.
 */
const LocationInstalledAtms = ({ installedAtms }: LocationInstalledAtmsProps) => {
  const navigate = useNavigate();

  if (!installedAtms || installedAtms.length === 0) {
    return <Chip label="No machine installed" />;
  }

  if (installedAtms.length === 1) {
    const atmId = installedAtms[0].atm_id.toString();
    return (
      <TableColumnClickableText
        id={atmId}
        to={`/machine/${atmId}`}
        text={`Machine ${atmId}`}
        fixIcon={true}
        clipboardValue={atmId}
        name="Machine ID"
        overrideTooltipLinkText="Go to Machine"
      />
    );
  }

  const visible = installedAtms.slice(0, VISIBLE_ATM_LIMIT);
  const overflow = installedAtms.slice(VISIBLE_ATM_LIMIT);

  return (
    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
      {visible.map((atm) => (
        <Link to={`/machine/${atm.atm_id}`} key={atm.atm_id}>
          <Chip label={`Machine ${atm.atm_id}`} sx={{ zIndex: 100 }} />
        </Link>
      ))}
      {overflow.length > 0 && (
        <BasePopover
          dataTest="installed-atms-overflow"
          buttonType="button"
          buttonText={`+${overflow.length} more`}
          buttonProps={{ size: 'small', variant: 'outlined', sx: { zIndex: 100 } }}
          items={overflow.map((atm) => `Machine ${atm.atm_id}`)}
          onClick={(index) => navigate(`/machine/${overflow[index].atm_id}`)}
        />
      )}
    </Stack>
  );
};

export default LocationInstalledAtms;
